import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { userService, resolveRole, ROLES } from '../services/userService';
import { setCurrentTenantId } from '../services/tenantContext';
import { logLoginEvent } from '../services/callableFunctions';
import { ApiAuthProvider } from './AuthContextV2';

// Phase 1.10 — feature flag selecting between the legacy Firebase impl
// (this file) and the new JWT/REST impl (AuthContextV2.js). Both populate
// the same React Context, so every `useAuth()` consumer is unchanged.
const AUTH_BACKEND = process.env.REACT_APP_AUTH_BACKEND || 'firebase';

// Fire-and-forget: every successful sign-in posts one row into the
// auditLog collection via the logLoginEvent callable. The callable
// silently no-ops if the user has no tenantId yet (fresh self-registered
// account), and we swallow network errors so login UX is never blocked.
const recordLogin = (user) => {
  if (!user?.uid) return;
  Promise.resolve()
    .then(() => logLoginEvent({}))
    .catch(() => { /* non-fatal */ });
};

export const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

const FirebaseAuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);

  const login = async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    recordLogin(cred?.user);
    return cred;
  };

  /**
   * Google sign-in. Opens the Google OAuth popup and returns a
   * UserCredential. A `users/{uid}` profile is NOT auto-created — if
   * the account is not linked to a tenant, the UI will still render but
   * show empty lists. Link the account via the admin panel or the
   * `scripts/linkGoogleUser.js` one-shot.
   */
  const loginWithGoogle = useCallback(async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const cred = await signInWithPopup(auth, provider);
    recordLogin(cred?.user);
    return cred;
  }, []);

  /**
   * Create a new email/password account and seed the matching
   * `users/{uid}` profile document so the app knows the user's role
   * from the very first login. Defaults to the PARENT role — staff
   * accounts are still provisioned by an admin.
   */
  const register = async ({ email, password, displayName, role = ROLES.PARENT }) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName && cred.user) {
      try {
        await updateProfile(cred.user, { displayName });
      } catch (_) {
        // Non-fatal — profile can still be set in Firestore below.
      }
    }
    await userService.upsertUserProfile(cred.user.uid, {
      role,
      displayName: displayName || null,
      email: cred.user.email || email || null
    });
    return cred;
  };

  const logout = () => {
    return signOut(auth);
  };

  const resetPassword = (email) => {
    return sendPasswordResetEmail(auth, email);
  };

  /**
   * Phone OTP — step 1. Creates a FRESH invisible reCAPTCHA verifier
   * per attempt, starts the phone sign-in flow, and returns a Firebase
   * `ConfirmationResult` that the caller passes back into
   * `confirmPhoneSignIn`.
   *
   * Why fresh-per-attempt? A RecaptchaVerifier instance is single-shot:
   * once it has solved the challenge (or failed) it cannot be reused.
   * Reusing a cached verifier was making every retry fail silently,
   * which looked to the user like "phone auth is broken".
   */
  const startPhoneSignIn = useCallback(async (phoneNumber, containerId = 'recaptcha-container') => {
    if (!phoneNumber) throw new Error('Phone number is required');

    // Clear any previous verifier so the DOM node is free and reCAPTCHA
    // can render a fresh challenge.
    if (window.__alBayanRecaptchaVerifier) {
      try { window.__alBayanRecaptchaVerifier.clear(); } catch (_) { /* ignore */ }
      window.__alBayanRecaptchaVerifier = null;
    }
    // Wipe anything reCAPTCHA left in the container so render() succeeds.
    const container = typeof document !== 'undefined'
      ? document.getElementById(containerId) : null;
    if (container) container.innerHTML = '';

    const verifier = new RecaptchaVerifier(auth, containerId, {
      size: 'invisible',
    });
    window.__alBayanRecaptchaVerifier = verifier;

    try {
      return await signInWithPhoneNumber(auth, phoneNumber, verifier);
    } catch (err) {
      // On failure, burn the verifier so the NEXT attempt gets a fresh
      // challenge instead of inheriting the broken state.
      try { verifier.clear(); } catch (_) { /* ignore */ }
      window.__alBayanRecaptchaVerifier = null;
      throw err;
    }
  }, []);

  /** Phone OTP — step 2. `confirmation` is the result from step 1. */
  const confirmPhoneSignIn = useCallback(async (confirmation, code) => {
    if (!confirmation) throw new Error('No pending phone confirmation');
    const cred = await confirmation.confirm(code);
    recordLogin(cred?.user);
    return cred;
  }, []);

  /**
   * Load the Firestore `users/{uid}` doc whenever the auth state
   * changes. Missing profiles are tolerated — the user is treated as
   * a TEACHER so Phase-1 accounts keep working unchanged.
   */
  const loadProfile = useCallback(async (user) => {
    if (!user) {
      setUserProfile(null);
      setCurrentTenantId(null);
      return;
    }
    setProfileLoading(true);
    try {
      const profile = await userService.getUserProfile(user.uid);
      setUserProfile(profile);
      // Services that build Firestore queries (classService, studentService,
      // sessionService...) read the tenantId from this module-level accessor
      // so they can add `where('tenantId', '==', ...)` to every list query.
      setCurrentTenantId(profile?.tenantId || null);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      await loadProfile(user);
      setAuthReady(true);
    });

    return unsubscribe;
  }, [loadProfile]);

  /**
   * Manually refresh the current user's profile (e.g. after role change,
   * or after an admin-bootstrap writes the user doc for the first time).
   * Falls back to `auth.currentUser` so it works even when React hasn't
   * yet flushed the `onAuthStateChanged` update into component state.
   */
  const refreshProfile = useCallback(async () => {
    const user = currentUser || auth.currentUser;
    if (user) await loadProfile(user);
  }, [currentUser, loadProfile]);

  const role = resolveRole(userProfile?.role);
  const linkedStudentIds = userProfile?.linkedStudentIds || [];
  const tenantId = userProfile?.tenantId || null;

  const value = {
    currentUser,
    userProfile,
    role,
    linkedStudentIds,
    tenantId,
    authReady,
    profileLoading,
    login,
    loginWithGoogle,
    register,
    logout,
    resetPassword,
    startPhoneSignIn,
    confirmPhoneSignIn,
    refreshProfile,
    ROLES
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Selector — switches implementation at module load. Toggle the flag in
// `.env.local` and restart `npm start` to flip backends.
export const AuthProvider = AUTH_BACKEND === 'api' ? ApiAuthProvider : FirebaseAuthProvider;

